/**
 * Inline marks of structured documents, schemaVersion 2
 * (docs/planning/agent-artifacts-phase2-plan-2026-09-24.md, section 6.4).
 *
 * In a version 2 document the text of paragraphs, list items, quotes,
 * callouts and table cells may use a strict inline subset:
 *
 *   **bold**   *italic*   [label](https://example.com)   \* (a literal star)
 *
 * Links take http, https or mailto targets only. A backslash makes the next
 * `\`, `*`, `[`, `]`, `(` or `)` literal. Anything that does not form a mark
 * (an unmatched star, a link to another scheme, a bracket without a target)
 * stays literal text, so parsing never fails and a typo never hides words.
 * Version 1 documents render their text literally and never reach this file.
 *
 * One parser for the three renderers (HTML, PDF, PPTX), both editors and the
 * limits (which count visible characters). Imported by the API, the mobile
 * app and the native node:test runner, so it has no runtime dependencies.
 */

/** A piece of text with the marks it carries. */
export interface InlineRun {
  text: string;
  bold?: true;
  italic?: true;
  /** http, https or mailto. */
  href?: string;
}

/** Characters a backslash makes literal. */
const ESCAPABLE = new Set(["\\", "*", "[", "]", "(", ")"]);

/** Longest link target kept as a link. */
export const INLINE_LINK_MAX_CHARS = 2_048;

type Token =
  | { kind: "text"; text: string }
  | { kind: "stars"; count: number; open: boolean; close: boolean }
  | { kind: "link"; href: string; children: Token[] };

type Tree =
  | { kind: "text"; text: string }
  | { kind: "strong" | "em"; children: Tree[] }
  | { kind: "link"; href: string; children: Tree[] };

/** An http, https or mailto target, or null. */
export function inlineLinkTarget(raw: string): string | null {
  const value = raw.trim();
  if (!value || value.length > INLINE_LINK_MAX_CHARS) return null;
  // eslint-disable-next-line no-control-regex -- control bytes are refused
  if (/[\s<>"\u0000-\u001f\u007f]/.test(value)) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol === "mailto:") return value;
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (!url.hostname || url.username || url.password) return null;
  return value;
}

const isSpace = (char: string | undefined) =>
  char === undefined || /\s/u.test(char);

/**
 * The end of a link starting at `start` (`[`), with its label range and
 * target, or null when the text there is not a link.
 */
function readLink(
  text: string,
  start: number,
): { labelEnd: number; end: number; href: string } | null {
  let index = start + 1;
  while (index < text.length) {
    const char = text[index];
    if (char === "\\" && ESCAPABLE.has(text[index + 1] ?? "")) {
      index += 2;
      continue;
    }
    if (char === "[") return null;
    if (char === "]") break;
    index += 1;
  }
  if (text[index] !== "]" || text[index + 1] !== "(") return null;
  const labelEnd = index;
  if (labelEnd === start + 1) return null;
  let target = "";
  index = labelEnd + 2;
  while (index < text.length) {
    const char = text[index];
    if (char === "\\" && ESCAPABLE.has(text[index + 1] ?? "")) {
      target += text[index + 1];
      index += 2;
      continue;
    }
    if (char === ")") break;
    if (char === "(") return null;
    target += char;
    index += 1;
  }
  if (text[index] !== ")") return null;
  const href = inlineLinkTarget(target);
  return href ? { labelEnd, end: index + 1, href } : null;
}

function tokenize(
  text: string,
  from: number,
  to: number,
  inLink: boolean,
): Token[] {
  const tokens: Token[] = [];
  let buffer = "";
  const flush = () => {
    if (buffer) tokens.push({ kind: "text", text: buffer });
    buffer = "";
  };
  let index = from;
  while (index < to) {
    const char = text[index];
    if (char === "\\" && index + 1 < to && ESCAPABLE.has(text[index + 1])) {
      buffer += text[index + 1];
      index += 2;
      continue;
    }
    if (char === "*") {
      let end = index;
      while (end < to && text[end] === "*") end += 1;
      const before = index === from ? undefined : text[index - 1];
      const after = end >= to ? undefined : text[end];
      flush();
      tokens.push({
        kind: "stars",
        count: end - index,
        open: !isSpace(after),
        close: !isSpace(before),
      });
      index = end;
      continue;
    }
    if (char === "[" && !inLink) {
      const link = readLink(text, index);
      if (link && link.end <= to) {
        flush();
        tokens.push({
          kind: "link",
          href: link.href,
          children: tokenize(text, index + 1, link.labelEnd, true),
        });
        index = link.end;
        continue;
      }
    }
    buffer += char;
    index += 1;
  }
  flush();
  return tokens;
}

type Slot = Tree | { kind: "opener"; count: number };

/** Match star runs into strong and em (a small subset of CommonMark's rule). */
function emphasis(tokens: Token[]): Tree[] {
  const out: Slot[] = [];
  const openers: number[] = [];
  for (const token of tokens) {
    if (token.kind === "text") {
      out.push(token);
      continue;
    }
    if (token.kind === "link") {
      out.push({
        kind: "link",
        href: token.href,
        children: emphasis(token.children),
      });
      continue;
    }
    let remaining = token.count;
    if (token.close) {
      while (remaining > 0 && openers.length > 0) {
        const at = openers[openers.length - 1];
        const opener = out[at] as { kind: "opener"; count: number };
        const use = remaining >= 2 && opener.count >= 2 ? 2 : 1;
        const children = out.splice(at + 1).map(settle);
        opener.count -= use;
        remaining -= use;
        out.push({ kind: use === 2 ? "strong" : "em", children });
        if (opener.count === 0) {
          out.splice(at, 1);
          openers.pop();
        }
      }
    }
    if (remaining > 0) {
      if (token.open) {
        openers.push(out.length);
        out.push({ kind: "opener", count: remaining });
      } else {
        out.push({ kind: "text", text: "*".repeat(remaining) });
      }
    }
  }
  return out.map(settle);
}

/** An opener nobody closed is literal stars. */
function settle(slot: Slot): Tree {
  return slot.kind === "opener"
    ? { kind: "text", text: "*".repeat(slot.count) }
    : slot;
}

function flatten(
  trees: Tree[],
  marks: Omit<InlineRun, "text">,
  runs: InlineRun[],
) {
  for (const tree of trees) {
    if (tree.kind === "text") {
      const last = runs[runs.length - 1];
      if (
        last &&
        last.bold === marks.bold &&
        last.italic === marks.italic &&
        last.href === marks.href
      ) {
        last.text += tree.text;
      } else if (tree.text) {
        runs.push({ text: tree.text, ...marks });
      }
    } else if (tree.kind === "link") {
      flatten(tree.children, { ...marks, href: tree.href }, runs);
    } else {
      flatten(
        tree.children,
        tree.kind === "strong"
          ? { ...marks, bold: true }
          : { ...marks, italic: true },
        runs,
      );
    }
  }
}

/**
 * The runs of one version 2 text. Never throws: what does not form a mark is
 * literal. Adjacent runs with the same marks are merged; empty runs dropped.
 */
export function parseInlineMarkup(text: string): InlineRun[] {
  const runs: InlineRun[] = [];
  flatten(emphasis(tokenize(text, 0, text.length, false)), {}, runs);
  return runs.map((run) => {
    const clean: InlineRun = { text: run.text };
    if (run.bold) clean.bold = true;
    if (run.italic) clean.italic = true;
    if (run.href) clean.href = run.href;
    return clean;
  });
}

/** What a reader sees: the text without the markup. */
export function inlineVisibleText(text: string): string {
  return parseInlineMarkup(text)
    .map((run) => run.text)
    .join("");
}

/** Visible characters (what the version 2 limits count). */
export function inlineVisibleLength(text: string): number {
  return inlineVisibleText(text).length;
}

/** A literal text as markup: every character the parser reads is escaped. */
export function escapeInlineText(text: string): string {
  return text.replace(/[\\*[\]]/g, (char) => `\\${char}`);
}

function escapeTarget(href: string): string {
  return href.replace(/[\\()]/g, (char) => `\\${char}`);
}

/**
 * Runs back to markup, so `parseInlineMarkup(formatInlineMarkup(runs))`
 * gives the same runs (the editors' round trip). Whitespace at the edge of
 * a bold or italic run moves outside the marks, where a closing star can
 * still close; a link with an unusable target keeps its words, unlinked.
 */
export function formatInlineMarkup(runs: readonly InlineRun[]): string {
  let out = "";
  let index = 0;
  while (index < runs.length) {
    const href = runs[index].href
      ? inlineLinkTarget(runs[index].href ?? "")
      : null;
    let end = index + 1;
    while (
      end < runs.length &&
      (runs[end].href ?? null) === (runs[index].href ?? null)
    ) {
      end += 1;
    }
    const group = runs.slice(index, end);
    const body = formatMarks(group);
    out += href && body.trim() ? `[${body}](${escapeTarget(href)})` : body;
    index = end;
  }
  return out;
}

function formatMarks(runs: readonly InlineRun[]): string {
  let out = "";
  let bold = false;
  let italic = false;
  const close = () => {
    if (italic) out += "*";
    if (bold) out += "**";
    bold = false;
    italic = false;
  };
  for (const run of runs) {
    if (!run.text) continue;
    const match = /^(\s*)([\s\S]*?)(\s*)$/u.exec(run.text);
    const lead = match?.[1] ?? "";
    const core = match?.[2] ?? "";
    const trail = match?.[3] ?? "";
    const wantBold = Boolean(run.bold) && core.length > 0;
    const wantItalic = Boolean(run.italic) && core.length > 0;
    if (lead || wantBold !== bold || wantItalic !== italic) close();
    out += escapeInlineText(lead);
    if (wantBold && !bold) {
      out += "**";
      bold = true;
    }
    if (wantItalic && !italic) {
      out += "*";
      italic = true;
    }
    out += escapeInlineText(core);
    if (trail) {
      close();
      out += escapeInlineText(trail);
    }
  }
  close();
  return out;
}
